import { ConfigError } from 'ProdantixSdk/core/errors';
import { trimTrailingSlash } from 'ProdantixSdk/core/http';
import { MemoryStorage } from 'ProdantixSdk/core/storage';
import { systemClock } from 'ProdantixSdk/core/time';
import type { Clock, Consent, EventProperties, IdFactory, ProdantixConfig, SocketFactory, StorageAdapter, Transport } from 'ProdantixSdk/core/types';
import { uuidFactory } from 'ProdantixSdk/core/uuid';
import { isProjectKey } from 'ProdantixSdk/core/validation';
import { SDK_NAME, SDK_VERSION } from 'ProdantixSdk/core/version';

export interface ResolvedConfig {
  apiKey: string;
  autocapture: boolean;
  clock: Clock;
  consent?: Consent;
  consentMode?: 'banner';
  defaultProperties: () => EventProperties;
  describeElement?: (element: unknown) => EventProperties;
  flagsHost: string;
  flushAt: number;
  flushIntervalMs: number;
  host: string;
  ids: IdFactory;
  locale?: string;
  maxQueueSize: number;
  maxRetries: number;
  namespace: string;
  onError: (error: unknown) => void;
  os?: string;
  requestTimeoutMs: number;
  sdkName: string;
  sdkVersion: string;
  sessionTracking: boolean;
  sharedStorage: StorageAdapter;
  socketFactory?: SocketFactory;
  storage: StorageAdapter;
  streamFlags: boolean;
  transport?: Transport;
}

const noop = (_error: unknown): void => undefined;

export function resolveConfig(config: ProdantixConfig): ResolvedConfig {
  if (!config.apiKey || !isProjectKey(config.apiKey)) {
    throw new ConfigError('apiKey must be a public project key of the form pdx_pub_<32 lowercase alphanumerics>');
  }
  if (!config.host) {
    throw new ConfigError('host is required (the ingest base URL)');
  }
  const host = trimTrailingSlash(config.host);
  const defaults = config.defaultProperties;
  const storage = config.storage ?? new MemoryStorage();
  return {
    apiKey: config.apiKey,
    autocapture: config.autocapture ?? false,
    clock: config.clock ?? systemClock,
    consent: config.consent,
    consentMode: config.consentMode,
    defaultProperties: typeof defaults === 'function' ? defaults : () => defaults ?? {},
    describeElement: config.describeElement,
    flagsHost: config.flagsHost ? trimTrailingSlash(config.flagsHost) : host,
    flushAt: config.flushAt ?? 20,
    flushIntervalMs: config.flushIntervalMs ?? 10_000,
    host,
    ids: config.ids ?? uuidFactory,
    locale: config.locale,
    maxQueueSize: config.maxQueueSize ?? 1000,
    maxRetries: config.maxRetries ?? 3,
    namespace: namespaceFor(config.apiKey),
    onError: config.onError ?? noop,
    os: config.os,
    requestTimeoutMs: config.requestTimeoutMs ?? 10_000,
    sdkName: config.sdkName ?? SDK_NAME,
    sdkVersion: config.sdkVersion ?? SDK_VERSION,
    sessionTracking: config.sessionTracking ?? false,
    sharedStorage: config.sharedStorage ?? storage,
    socketFactory: config.socketFactory,
    storage,
    streamFlags: config.streamFlags ?? false,
    transport: config.transport,
  };
}

export function namespaceFor(apiKey: string): string {
  return apiKey.slice(0, 16);
}
